import React from "react";
import { Card, Table, Badge } from "react-bootstrap";
import { ChevronDown, ExclamationCircle } from "react-bootstrap-icons";
import { Currency } from "../../../lib/components/Currency";
import { FormattedDate } from "../../../lib/components/FormattedDate";
import { CheckBox } from "./CheckBox";
import { Actions } from "./Actions";
import { UNASSIGNED, CommissionStatus } from "./constants";
import { Link } from "react-router-dom";

export function AgentGroupCard({
  groupKey,
  rows,
  selected,
  onToggleOne,
  onToggleGroup,
  isCollapsed,
  onToggleCollapse,
  busy,
  errored,
  onUpdateStatus,
}) {
  const unassigned = groupKey === UNASSIGNED;
  const pend = rows.filter((c) => c.commissionStatus === CommissionStatus.Pending);
  const pendIds = pend.map((c) => c.number);
  const pendAmount = pend.reduce((s, c) => s + c.netPrice, 0);
  const groupSelCount = pendIds.filter((id) => selected.has(id)).length;
  const allSel = pendIds.length > 0 && groupSelCount === pendIds.length;
  const someSel = groupSelCount > 0 && !allSel;
  const label = unassigned ? "Unassigned" : groupKey;

  return (
    <Card className="shadow-sm overflow-hidden">
      {/* Group header — click to collapse/expand */}
      <Card.Header
        role="button"
        tabIndex={0}
        aria-expanded={!isCollapsed}
        onClick={onToggleCollapse}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggleCollapse();
          }
        }}
        className="d-flex align-items-center gap-3 border-bottom-0 user-select-none"
        style={{
          cursor: "pointer",
          borderLeft: `3px solid var(--bs-${
            unassigned ? "warning" : "success"
          })`,
          backgroundColor: `var(--bs-${
            unassigned ? "warning-bg-subtle" : "tertiary-bg"
          })`,
        }}
      >
        <ChevronDown
          size={14}
          className="flex-shrink-0 text-secondary"
          style={{
            transition: "transform .15s",
            transform: isCollapsed ? "rotate(-90deg)" : "none",
          }}
        />
        <div className="flex-grow-1 min-w-0">
          <h2 className="h6 fw-semibold mb-0 text-truncate">{label}</h2>
          {unassigned && (
            <p className="d-flex align-items-center gap-1 small text-warning-emphasis mb-0 mt-1">
              <ExclamationCircle size={14} />
              No agent, these can only be declined
            </p>
          )}
        </div>
        {groupSelCount > 0 && (
          <Badge
            bg="success-subtle"
            text="success-emphasis"
            className="flex-shrink-0"
          >
            {groupSelCount} selected
          </Badge>
        )}
        <div className="text-end flex-shrink-0">
          <p className="fw-semibold tnum mb-0">
            <Currency amount={pendAmount} />
          </p>
          <p
            className="text-uppercase text-secondary mb-0"
            style={{ fontSize: 11, letterSpacing: "0.04em" }}
          >
            pending
          </p>
        </div>
      </Card.Header>

      {/* Rows */}
      {!isCollapsed && (
        <Table
          hover
          responsive
          className="mb-0 align-middle"
          style={{ tableLayout: "fixed", minWidth: 720 }}
        >
          <colgroup>
            <col style={{ width: 48 }} />
            <col style={{ width: 116 }} />
            <col style={{ width: 130 }} />
            <col style={{ width: 110 }} />
            <col />
            <col />
            <col style={{ width: 110 }} />
            <col style={{ width: 190 }} />
          </colgroup>
          <thead>
            <tr
              className="small text-uppercase text-secondary"
              style={{ fontSize: 11, letterSpacing: "0.04em" }}
            >
              <th className="text-center fw-medium">
                {pendIds.length > 0 && (
                  <CheckBox
                    checked={allSel}
                    indeterminate={someSel}
                    onChange={() => onToggleGroup(pendIds, !allSel)}
                    ariaLabel={`Select all pending for ${label}`}
                  />
                )}
              </th>
              <th className="fw-medium">Signed</th>
              <th className="fw-medium">Provider #</th>
              <th className="fw-medium">Contract #</th>
              <th className="fw-medium">Recipient(s)</th>
              <th className="fw-medium">Purchaser</th>
              <th className="fw-medium text-end">Net price</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => {
              const isPending = c.commissionStatus === CommissionStatus.Pending;
              const isSel = selected.has(c.number);
              return (
                <tr
                  key={c.number}
                  className={isSel ? "row-selected" : undefined}
                >
                  <td className="text-center">
                    {isPending && (
                      <CheckBox
                        checked={isSel}
                        onChange={() => onToggleOne(c.number)}
                        ariaLabel={`Select contract ${c.number}`}
                      />
                    )}
                  </td>
                  <td className="tnum">
                    <FormattedDate date={c.signed} />
                  </td>
                  <td className="tnum">
                    <Link to={`/contracts/${c.number}`} target="_blank">
                      {c.providerNumber}
                    </Link>
                  </td>
                  <td className="tnum">
                    <Link to={`/contracts/${c.number}`} target="_blank">
                      {c.number}
                    </Link>
                  </td>
                  <td className="text-truncate">
                    {c.recipients.map((r, ri) => (
                      <div key={ri}>
                        {r.firstName} {r.lastName}
                        <br />
                      </div>
                    ))}
                  </td>
                  <td className="text-truncate">
                    {c.purchaser.firstName} {c.purchaser.lastName}
                  </td>
                  <td className="tnum text-end">
                    <Currency amount={c.netPrice} />
                  </td>
                  <td className="text-end">
                    <Actions
                      contract={c}
                      unassigned={unassigned}
                      busy={busy.has(c.number)}
                      errored={errored.has(c.number)}
                      onApprove={() => onUpdateStatus([c.number], CommissionStatus.Approved)}
                      onDecline={() => onUpdateStatus([c.number], CommissionStatus.Declined)}
                      onReset={() => onUpdateStatus([c.number], CommissionStatus.Pending)}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Card>
  );
}
